import React, { useRef } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';

import Typography from '@mui/material/Typography';

import {
  FIELD_COLOR,
  MENU_BACKGROUND_COLOR,
  SuggestionLabel,
  SuggestionLabelColor,
} from '../constants';
import { TipsMenuItem } from './TipsMenuItem';

interface SuggestionGroup {
  id?: string;
  isLoading?: boolean;
  options: string[];
  type: string;
}

interface SuggestionMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (type: string, option: string) => void;
  open: boolean;
  suggestions: SuggestionGroup[];
}

export const SuggestionMenu = React.forwardRef<HTMLUListElement, SuggestionMenuProps>(
  ({ anchorEl, onClose, onSelect, open, suggestions }, ref) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const firstItemRef = useRef<HTMLLIElement>(null);

    if (!anchorEl || !open) {
      return null;
    }

    const handleSelect = (type: string, option: string) => {
      onSelect(type, option);
    };

    return (
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        disablePortal={false}
        onClose={() => {
          onClose();
        }}
        open={open}
        slotProps={{
          paper: {
            style: {
              backgroundColor: 'transparent',
              marginTop: 1,
              overflow: 'hidden',
              width: anchorEl ? `${anchorEl.offsetWidth}px` : 'auto',
            },
          },
        }}
        style={{ zIndex: 1500 }}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
      >
        <Paper
          ref={menuRef}
          sx={{
            backgroundColor: MENU_BACKGROUND_COLOR,
            borderRadius: 0.5,
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '80vh',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
          }}
        >
          <MenuList
            ref={ref}
            sx={{
              '& .MuiMenuItem-root': {
                textAlign: 'left',
              },
              flexGrow: 1,
              maxHeight: '400px',
              overflowY: 'auto',
              paddingTop: 0,
            }}
            tabIndex={-1}
          >
            {suggestions.flatMap(({ options, ...suggestion }, suggestionIndex) => {
              const noOptions = options.length === 0;
              const { isLoading } = suggestion;

              const parsedOptions: React.ReactNode[] = [];

              if (isLoading && options.length === 0) {
                parsedOptions.push(
                  <MenuItem
                    key={`loading-empty-${suggestionIndex}`}
                    dense={true}
                    disabled={true}
                    sx={{ pl: 2 }}
                  >
                    <Box sx={{ alignItems: 'center', display: 'flex' }}>
                      <CircularProgress
                        size={16}
                        sx={{
                          color: 'text.disabled',
                          marginRight: 1,
                        }}
                      />
                      <ListItemText
                        primary="Loading suggestions..."
                        sx={{
                          color: 'text.disabled',
                          fontSize: '0.9rem',
                          fontStyle: 'italic',
                        }}
                      />
                    </Box>
                  </MenuItem>,
                );
              } else if (!isLoading && noOptions) {
                parsedOptions.push(
                  <MenuItem
                    key={`empty-${suggestion.id || suggestion.type}-${suggestionIndex}`}
                    dense={true}
                    disabled={true}
                    sx={{ pl: 2 }}
                  >
                    <ListItemText
                      primary="No options available"
                      sx={{
                        color: 'text.disabled',
                        fontSize: '0.9rem',
                        fontStyle: 'italic',
                      }}
                    />
                  </MenuItem>,
                );
              } else {
                options.map((option, index) => {
                  let absoluteIndex = 0;
                  for (let i = 0; i < suggestionIndex; i++) {
                    const prevGroup = suggestions[i];
                    if (prevGroup.options.length > 0 || prevGroup.isLoading) {
                      absoluteIndex += Math.max(prevGroup.options.length, 1);
                    }
                  }
                  absoluteIndex += index;

                  const isFirstItem = absoluteIndex === 0;
                  const itemKey = `${suggestion.id || suggestion.type}-${option}-${suggestionIndex}-${index}`;

                  parsedOptions.push(
                    <MenuItem
                      key={itemKey}
                      ref={isFirstItem ? firstItemRef : null}
                      dense={true}
                      disabled={suggestion.isLoading}
                      onClick={() => !suggestion.isLoading && handleSelect(suggestion.type, option)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      sx={{
                        color: suggestion.isLoading
                          ? 'text.disabled'
                          : SuggestionLabelColor[suggestion.type] || FIELD_COLOR,
                        opacity: suggestion.isLoading ? 0.7 : 1,
                        pl: 2,
                      }}
                      tabIndex={suggestion.isLoading ? -1 : 0}
                    >
                      <ListItemText
                        primary={option}
                        sx={{
                          color: 'inherit',
                          fontSize: '0.9rem',
                          fontStyle: suggestion.isLoading ? 'italic' : 'normal',
                        }}
                      />
                    </MenuItem>,
                  );
                });
              }

              return [
                <ListSubheader
                  key={`header-${suggestion.id || suggestion.type}-${suggestionIndex}`}
                  sx={{
                    backgroundColor: MENU_BACKGROUND_COLOR,
                    position: 'sticky',
                    top: 2,
                    zIndex: 2,
                  }}
                >
                  <Typography
                    color="secondary.main"
                    sx={{
                      lineHeight: 2,
                      textTransform: 'uppercase',
                    }}
                    variant="body2Bold"
                  >
                    {SuggestionLabel[suggestion.type]}
                    {suggestion.isLoading && (
                      <Box
                        component="span"
                        sx={{
                          alignItems: 'center',
                          display: 'inline-flex',
                          fontSize: '0.75rem',
                          fontStyle: 'italic',
                          marginLeft: 1,
                          opacity: 0.7,
                        }}
                      >
                        <CircularProgress
                          size={12}
                          sx={{
                            color: 'secondary.light',
                            marginRight: 0.5,
                          }}
                        />
                        Loading...
                      </Box>
                    )}
                  </Typography>
                </ListSubheader>,
                ...parsedOptions,
              ];
            })}
          </MenuList>
          <TipsMenuItem />
        </Paper>
      </Popover>
    );
  },
);

SuggestionMenu.displayName = 'SuggestionMenu';
